import { Fragment, useMemo, useState, type ReactElement } from 'react';
import { Badge, Button, ListPager, Table, TableWrap, TBody, TD, TH, THead, TR, Tooltip } from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import { cx } from '@/lib/cx';
import type { SchoolProfileRow } from '@/lib/types';
import { BarList, type BarListRow } from '../fab-ui';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiExpandButton,
  PiLoading,
  PiSection,
  PiSortHeader,
  piCount,
} from '../pi-kit';
import {
  countryRows,
  interactionRows,
  programmeRows,
  SCHOOL_PROFILES_PER_PAGE,
  schoolKey,
  TIER_LABEL,
  VIEWS_STATUS_LABEL,
  viewsBreakdownOk,
  viewsChannelRows,
  viewsCountryRows,
  viewsUtmSourceRows,
} from './school-profiles-model';
import type { SchoolProfilesView } from './use-school-profiles-data';

/**
 * School Profiles per-school table. Rows arrive in the server's order (engaged
 * students, descending) and PAGE at the upstream ceiling of 100.
 *
 * - **Sorting only when the whole set is loaded** (one page, or Show all that
 *   was not truncated). A client sort of one page of N would present that page
 *   as the whole, so on a partial set the headers are plain labels.
 * - The school is SEED's; the EMERGE company (resolved server-side) is the
 *   link when there is one.
 * - `views` / `visitors` are `null` unless `viewsStatus` is `ok`; a blank
 *   renders a dash with the reason on hover, never a `0`. Nulls sort last.
 * - A row expands inline to its top programmes, top countries and interaction
 *   mix, plus profile views by country, channel and UTM source when
 *   `meta.viewsBreakdownStatus === 'ok'` (absent → hidden; `null` → "—").
 * - 6 data columns, so no ColumnManager. The school column is frozen and
 *   carries the expand chevron.
 * - The rendered list is capped at 50 rows with "Show more" (the page itself
 *   can hold 100, or many more under Show all).
 */

const COL_COUNT = 6;
const DETAIL_ROWS = 8;
const RENDER_STEP = 50;

type SortKey = 'school' | 'views' | 'visitors' | 'engagedStudents' | 'interactions';
type SortState = { key: SortKey; direction: 'asc' | 'desc' } | null;

const NUMERIC_COLUMNS: readonly { key: Exclude<SortKey, 'school'>; label: string; width: string }[] = [
  { key: 'views', label: 'Views', width: 'min-w-20' },
  { key: 'visitors', label: 'Visitors', width: 'min-w-20' },
  { key: 'engagedStudents', label: 'Engaged students', width: 'min-w-32' },
  { key: 'interactions', label: 'Interactions', width: 'min-w-24' },
];

function displayName(row: SchoolProfileRow): string {
  return row.company?.name ?? row.schoolName;
}

function numericOf(row: SchoolProfileRow, key: Exclude<SortKey, 'school'>): number | null {
  return row[key];
}

function sortRows(
  rows: readonly { row: SchoolProfileRow; key: string }[],
  sort: SortState,
): readonly { row: SchoolProfileRow; key: string }[] {
  if (sort === null) return rows;
  const sign = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (sort.key === 'school') {
      return sign * displayName(a.row).localeCompare(displayName(b.row), undefined, { sensitivity: 'base' });
    }
    const av = numericOf(a.row, sort.key);
    const bv = numericOf(b.row, sort.key);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return sign * (av - bv);
  });
}

function ViewsBreakdownList({
  title,
  rows,
}: {
  title: string;
  rows: BarListRow[] | null;
}): ReactElement {
  if (rows === null) return <BarList title={title} total={null} rows={[]} emptyText="—" />;
  return <BarList title={title} rows={rows} emptyText="No profile views." maxRows={DETAIL_ROWS} />;
}

function TierBadge({ row }: { row: SchoolProfileRow }): ReactElement {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {row.tier !== null ? (
        <Badge tone={row.tier === 'premium' ? 'accent' : 'neutral'}>{TIER_LABEL[row.tier]}</Badge>
      ) : (
        <span className="text-fg-muted">—</span>
      )}
      {!row.profilePublic && (
        <Badge tone="neutral" variant="outline">
          Not public
        </Badge>
      )}
    </span>
  );
}

function SchoolName({ row }: { row: SchoolProfileRow }): ReactElement {
  const company = row.company ?? null;
  return (
    <div className="min-w-0">
      {company !== null ? (
        <TextLink to={`/companies/${company.id}`} className="block truncate font-medium">
          {company.name}
        </TextLink>
      ) : (
        <div className="truncate font-medium text-fg">{row.schoolName}</div>
      )}
      {company !== null && company.name !== row.schoolName && (
        <div className="truncate text-xs text-fg-muted">{row.schoolName}</div>
      )}
    </div>
  );
}

function ViewsValue({ row, value }: { row: SchoolProfileRow; value: number | null }): ReactElement {
  if (value !== null) return <span className="tabular-nums">{piCount(value)}</span>;
  const reason =
    row.viewsStatus === null || row.viewsStatus === 'ok' ? undefined : VIEWS_STATUS_LABEL[row.viewsStatus];
  if (reason === undefined) return <span className="text-fg-muted">—</span>;
  return (
    <Tooltip label={reason}>
      <span className="text-fg-muted">—</span>
    </Tooltip>
  );
}

function SchoolDetail({ row, breakdownOk }: { row: SchoolProfileRow; breakdownOk: boolean }): ReactElement {
  const byCountry = row.viewsByCountry;
  const bySource = row.viewsBySource;
  const showBreakdown = breakdownOk && (byCountry !== undefined || bySource !== undefined);
  return (
    <div className="grid min-w-0 gap-3">
      <div className="grid min-w-0 gap-3 md:grid-cols-3">
        <BarList title="Top programmes" rows={programmeRows(row)} emptyText="No programme interest." />
        <BarList title="Top countries" rows={countryRows(row.topCountries)} emptyText="No engaged students." />
        <BarList title="Interaction mix" rows={interactionRows(row.byType)} emptyText="No interactions." />
      </div>
      {showBreakdown && (
        <div className="grid min-w-0 gap-3 md:grid-cols-3">
          {byCountry !== undefined && (
            <ViewsBreakdownList
              title="Views by country"
              rows={byCountry === null ? null : viewsCountryRows(byCountry)}
            />
          )}
          {bySource !== undefined && (
            <ViewsBreakdownList
              title="Views by channel"
              rows={bySource === null ? null : viewsChannelRows(bySource.channel)}
            />
          )}
          {bySource !== undefined && (
            <ViewsBreakdownList
              title="Views by source"
              rows={bySource === null ? null : viewsUtmSourceRows(bySource.utmSource)}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function SchoolProfilesTable({
  view,
  loading,
  refreshing,
  showAll,
  onPage,
  onToggleShowAll,
}: {
  view: SchoolProfilesView | null;
  loading: boolean;
  refreshing: boolean;
  showAll: boolean;
  onPage: (page: number) => void;
  onToggleShowAll: (next: boolean) => void;
}): ReactElement {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [sort, setSort] = useState<SortState>(null);
  const viewKey = view === null ? '' : `${view.page}-${showAll ? 'all' : 'page'}-${view.total}`;
  const [rendered, setRendered] = useState<{ key: string; count: number }>({ key: '', count: RENDER_STEP });
  const renderCount = rendered.key === viewKey ? rendered.count : RENDER_STEP;

  const breakdownOk = viewsBreakdownOk(view?.response.meta);
  const wholeSet = view !== null && (view.totalPages <= 1 || (showAll && !view.truncated));
  const activeSort = wholeSet ? sort : null;

  const keyed = useMemo(() => {
    if (view === null) return [];
    const offset = showAll ? 0 : (view.page - 1) * SCHOOL_PROFILES_PER_PAGE;
    return view.schools.map((row, index) => ({ row, key: schoolKey(row, offset + index) }));
  }, [view, showAll]);
  const sorted = useMemo(() => sortRows(keyed, activeSort), [keyed, activeSort]);
  const visible = sorted.slice(0, renderCount);
  const hiddenCount = sorted.length - visible.length;

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const onSort = (key: SortKey) =>
    setSort((prev) => {
      if (prev === null || prev.key !== key) return { key, direction: key === 'school' ? 'asc' : 'desc' };
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });

  const header = (key: SortKey, label: string, align: 'left' | 'right') =>
    wholeSet ? (
      <PiSortHeader
        label={label}
        active={activeSort?.key === key}
        direction={activeSort?.key === key ? activeSort.direction : align === 'left' ? 'asc' : 'desc'}
        onClick={() => onSort(key)}
        align={align}
      />
    ) : (
      label
    );

  const expandButton = (row: SchoolProfileRow, key: string) => {
    const open = expanded.has(key);
    const name = displayName(row);
    return (
      <PiExpandButton
        expanded={open}
        onClick={() => toggle(key)}
        label={open ? `Collapse ${name}` : `Expand ${name}`}
      />
    );
  };

  let body: ReactElement;
  if (view === null) {
    body = (
      <div aria-busy={loading || undefined}>
        <PiLoading variant="table" rows={8} />
      </div>
    );
  } else if (view.schools.length === 0) {
    body = <PiEmpty title="No subscribed schools for this selection." />;
  } else {
    body = (
      <div className={cx(refreshing && 'opacity-70')} aria-busy={refreshing || undefined}>
        <div className="max-md:hidden">
          <TableWrap frozenHeader>
            <Table>
              <THead>
                <TR>
                  <TH frozen="left" className="min-w-72">
                    {header('school', 'School', 'left')}
                  </TH>
                  <TH className="min-w-32">Tier</TH>
                  {NUMERIC_COLUMNS.map((column) => (
                    <TH
                      key={column.key}
                      numeric
                      className={column.width}
                      aria-sort={
                        activeSort?.key === column.key
                          ? activeSort.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                    >
                      {header(column.key, column.label, 'right')}
                    </TH>
                  ))}
                </TR>
              </THead>
              <TBody>
                {visible.map(({ row, key }) => {
                  const open = expanded.has(key);
                  return (
                    <Fragment key={key}>
                      <TR>
                        <TD frozen="left" className="max-w-96 pl-1">
                          <div className="flex min-w-0 items-center gap-1">
                            <div className="shrink-0">{expandButton(row, key)}</div>
                            <SchoolName row={row} />
                          </div>
                        </TD>
                        <TD>
                          <TierBadge row={row} />
                        </TD>
                        <TD numeric className="tabular-nums">
                          <ViewsValue row={row} value={row.views} />
                        </TD>
                        <TD numeric className="tabular-nums">
                          <ViewsValue row={row} value={row.visitors} />
                        </TD>
                        <TD numeric className="font-medium tabular-nums">
                          {piCount(row.engagedStudents)}
                        </TD>
                        <TD numeric className="tabular-nums">
                          {piCount(row.interactions)}
                        </TD>
                      </TR>
                      {open && (
                        <TR>
                          <TD colSpan={COL_COUNT} className="bg-surface-sunken">
                            <div className="pl-9">
                              <SchoolDetail row={row} breakdownOk={breakdownOk} />
                            </div>
                          </TD>
                        </TR>
                      )}
                    </Fragment>
                  );
                })}
              </TBody>
            </Table>
          </TableWrap>
        </div>

        <div className="p-3 md:hidden">
          <PiCardList
            items={visible}
            getKey={(item) => item.key}
            render={({ row, key }) => (
              <div className="grid min-w-0 gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <SchoolName row={row} />
                    <div className="mt-1">
                      <TierBadge row={row} />
                    </div>
                  </div>
                  <div className="shrink-0">{expandButton(row, key)}</div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  <PiCardField label="Views" value={<ViewsValue row={row} value={row.views} />} />
                  <PiCardField label="Visitors" value={<ViewsValue row={row} value={row.visitors} />} />
                  <PiCardField
                    label="Engaged students"
                    value={<span className="tabular-nums">{piCount(row.engagedStudents)}</span>}
                  />
                  <PiCardField
                    label="Interactions"
                    value={<span className="tabular-nums">{piCount(row.interactions)}</span>}
                  />
                </div>
                {expanded.has(key) && (
                  <div className="border-t border-hairline pt-3">
                    <SchoolDetail row={row} breakdownOk={breakdownOk} />
                  </div>
                )}
              </div>
            )}
          />
        </div>

        {hiddenCount > 0 && (
          <div className="flex justify-center border-t border-hairline p-3">
            <Button
              variant="secondary"
              className="max-sm:h-11"
              onClick={() => setRendered({ key: viewKey, count: renderCount + RENDER_STEP })}
            >
              Show {piCount(Math.min(RENDER_STEP, hiddenCount))} more
            </Button>
          </div>
        )}
      </div>
    );
  }

  const firstRow =
    view === null || view.schools.length === 0
      ? 0
      : showAll
        ? 1
        : (view.page - 1) * SCHOOL_PROFILES_PER_PAGE + 1;
  const lastRow = view === null ? 0 : firstRow === 0 ? 0 : firstRow + view.schools.length - 1;

  return (
    <PiSection title="Schools" count={view?.total ?? null} flush id="school-profiles-schools">
      {body}
      {view !== null && view.total > 0 && (
        <ListPager
          page={view.page}
          totalPages={view.totalPages}
          total={view.total}
          firstRow={firstRow}
          lastRow={lastRow}
          perPage={SCHOOL_PROFILES_PER_PAGE}
          showAll={showAll}
          loading={refreshing}
          onPage={onPage}
          onToggleShowAll={onToggleShowAll}
          noun="schools"
          truncated={view.truncated}
        />
      )}
    </PiSection>
  );
}
